import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {
  computeCanAddRows,
  computeCanModifyColumnDim,
  computeCanModifyDims,
  computeCanModifyRowDim,
  computeCanRemoveRows,
  computeFirstIndex,
  computeGhostCellsCount,
  computeHasLockedRowCount,
  computeLastIndex,
  computeShouldShowGhostCells,
} from 'notebook/tables/store/logic/TableDimensionsLogic';
import {createTableModelSelector} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore, TableEditableVolatileProps} from 'notebook/tables/store/TableStore';

import {TableContextModel} from './TableContextSelectors';
import {TableSelectionModel} from './TableSelectionSelectors';
import {TableViewsModel} from './TableViewsSelectors';

/**
 * Model containing computed dimension flags and structural bounds for a table.
 * These flags indicate what dimension operations are allowed on the table based on its current state,
 * viewer mode, lock status, and dimension constraints.
 */
export interface TableDimensionsModel {
  /**
   * Whether the table dimensions (rows AND columns) can be modified.
   * Returns false when ANY dimension is locked.
   * Consider using canModifyColumnDim or canModifyRowDim for specificity.
   */
  canModifyDims: boolean;

  /**
   * Whether the table's horizontal (column) dimension can be modified.
   * Returns true when columns can be added, removed, or reordered.
   */
  canModifyColumnDim: boolean;

  /**
   * Whether the table's vertical (row) dimension can be modified.
   * Returns true when rows can be added or removed.
   */
  canModifyRowDim: boolean;

  /**
   * Whether rows can be removed from the table.
   * Takes into account viewer mode, table lock state, placeholder rows,
   * table settings, and whether a delete rows handler is available.
   */
  canRemoveRows: boolean;

  /**
   * Whether rows can be added to the table.
   * Takes into account viewer mode, table lock state, table settings,
   * and locked row count metadata.
   */
  canAddRows: boolean;

  /**
   * Whether the table has a locked row count from metadata.
   * This is ignored for config tables.
   */
  hasLockedRowCount: boolean;

  /**
   * The first cell index of the table [0, 0].
   */
  firstIndex: TableIndex;

  /**
   * The last cell index of the table [numRows - 1, numCols - 1].
   * Reflects actual data bounds, not ghost cells.
   */
  lastIndex: TableIndex;

  /**
   * Whether ghost cells (empty placeholder columns) should be displayed to the right
   * of the table's real data columns. Requires that column modifications are allowed
   * and that either a multi-cell selection exists or the table is maximized/split-screen.
   */
  shouldShowGhostCells: boolean;

  /**
   * The number of ghost cells (empty placeholder columns) to render to the right of
   * the table's real data columns. Returns 0 when ghost cells should not be shown.
   */
  ghostCellsCount: number;
}

/**
 * Creates a memoized selector that computes the TableDimensionsModel from the store.
 *
 * @param volatilePropsSelector - Selector to extract volatile props from the store
 * @param tableContextSelector - Selector to extract table context from the store
 * @param tableSelectionSelector - Selector to extract selection state from the store
 * @param tableViewsSelector - Selector to extract views state from the store
 */
export function createTableDimensionsSelector(
  volatilePropsSelector: (store: TableEditableStore) => TableEditableVolatileProps,
  tableContextSelector: (store: TableEditableStore) => TableContextModel,
  tableSelectionSelector: (store: TableEditableStore) => TableSelectionModel,
  tableViewsSelector: (store: TableEditableStore) => TableViewsModel
): (store: TableEditableStore) => TableDimensionsModel {
  return createTableModelSelector(
    [
      (store) => volatilePropsSelector(store).table,
      (store) => tableContextSelector(store).isConfigTable,
      (store) => store.stableProps.viewer ?? false,
      (store) => store.stableProps.locked ?? false,
      (store) => store.stableProps.hasPlaceholderRows ?? false,
      (store) => store.stableProps.canAddRemoveColumns ?? true,
      (store) => store.stableProps.canAddColumnsFreeform ?? true,
      (store) => tableSelectionSelector(store).hasMultiSelection,
      (store) => tableViewsSelector(store).isMaximizedOrSplitScreen,
    ],
    (
      table,
      isConfigTable,
      viewer,
      locked,
      hasPlaceholderRows,
      canAddRemoveColumns,
      canAddColumnsFreeform,
      hasMultiSelection,
      isMaximizedOrSplitScreen
    ) => {
      const canModifyColumnDim = computeCanModifyColumnDim(table, viewer, locked);
      const shouldShowGhostCells = computeShouldShowGhostCells({
        canModifyColumnDim,
        canAddRemoveColumns,
        canAddColumnsFreeform,
        hasMultiSelection,
        isMaximizedOrSplitScreen,
      });
      return {
        canModifyDims: computeCanModifyDims(table, viewer, locked),
        canModifyColumnDim,
        canModifyRowDim: computeCanModifyRowDim(table, viewer, locked),
        canRemoveRows: computeCanRemoveRows(table, viewer, locked, hasPlaceholderRows),
        canAddRows: computeCanAddRows(table, viewer, locked, isConfigTable),
        hasLockedRowCount: computeHasLockedRowCount(table, isConfigTable),
        firstIndex: computeFirstIndex(),
        lastIndex: computeLastIndex(table),
        shouldShowGhostCells,
        ghostCellsCount: computeGhostCellsCount(table, shouldShowGhostCells),
      };
    }
  );
}
